import React, { useState, useEffect, useMemo } from "react";
import { Navbar } from "../../components/Navbar";
import { EventPreviewModal } from "../../components/EventPreviewModal";
import { Event } from "../../components/EventCard";
import { AnimatePresence } from "../../components/AnimatePresenceWrapper";
import { Login } from "../../components/Login";
import { OrganizationHomepageThemeOne } from "../../components/themes/OrganizationHomepageThemeOne";
import { OrganizationHomepageThemeTwo } from "../../components/themes/OrganizationHomepageThemeTwo";
import { OrganizationHomepageThemeThree } from "../../components/themes/OrganizationHomepageThemeThree";
import { OrganizationHomepageThemeFour } from "../../components/themes/OrganizationHomepageThemeFour";
import { useParams } from "react-router-dom";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { IOrganizationProps } from "../../types/Organization";
import { Endpoints } from "../../lib/endpoints";
import { ISmallEventProps } from "../../types";
import { FullPageLoader } from "../../components/ui/loader";
import {
  useUser,
  getOrgTokenKey,
  getOrgUserProfileKey,
} from "../../context/UserContext";
import { IThemeProps } from "../../types/Theme";
import {
  isCustomDomain,
  getDomainForLookup,
  getOrganizationSlugFromSubdomain,
  isSubdomain,
} from "../../lib/utils/domain";
import OrganizationNotFound from "../OrganizationNotFound";
import { PuckRenderer } from "../../components/PageBuilder/PuckRenderer";
import OrganizationHomepageTheDRA from "../OrganizationHomepageTheDRA";

// Convert ISmallEventProps to Event format
const convertToEvent = (event: ISmallEventProps): Event => {
  // Format time from start_time and end_time (e.g., "09:00" to "9:00 AM - 5:00 PM")
  const formatTime = (timeString: string): string => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const startTime = formatTime(event.start_time);
  const endTime = formatTime(event.end_time);
  const timeDisplay =
    startTime && endTime ? `${startTime} - ${endTime}` : startTime || "TBD";

  // Use the date as-is (ISO format), EventCard can parse it
  // Ensure the date is valid, otherwise use empty string
  const formattedDate =
    event.date && !isNaN(new Date(event.date).getTime())
      ? event.date
      : new Date().toISOString();

  // Determine location based on event type
  const getLocationString = (): string => {
    switch (event.type) {
      case "online":
        return "Online Event";
      case "hybrid":
        return "Hybrid Event";
      case "offline":
        return "Venue TBA";
      default:
        return "Location TBA";
    }
  };

  return {
    id: event.id.toString(),
    title: event.name,
    description: event.short_description || "",
    date: formattedDate,
    time: timeDisplay,
    location: getLocationString(),
    image: event.cover_image || "",
    category: event.category || "General",
    attendees: event.tickets_sold || 0,
    maxAttendees: event.total_tickets || undefined,
  };
};

const OrganizationHomepage: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const { organizationSlug: urlSlug } = useParams();
  const { fetchUserProfile, userProfile, clearUserProfile, setUserProfile } =
    useUser();

  // Get organization slug from subdomain if we're on a subdomain
  const subdomainSlug = getOrganizationSlugFromSubdomain();
  const isOnSubdomain = isSubdomain();

  // Use subdomain slug if available, otherwise use URL slug
  const organizationSlug = subdomainSlug || urlSlug;

  // Determine if we're on a custom domain or using slug-based routing
  const usingCustomDomain = isCustomDomain();
  const domainForLookup = getDomainForLookup();

  // Choose the appropriate endpoint based on whether we're on a custom domain or subdomain
  const endpoint = useMemo(() => {
    if (usingCustomDomain && !isOnSubdomain) {
      // Use domain-based lookup for actual custom domains (not subdomains)
      return Endpoints.GetOrganizationUsingWebsite(domainForLookup);
    } else {
      // Use slug-based lookup (for subdomains or URL-based routing)
      if (!organizationSlug) {
        // Fallback - shouldn't happen but handle gracefully
        return Endpoints.GetSingleOrganization("");
      }
      return Endpoints.GetSingleOrganization(organizationSlug);
    }
  }, [usingCustomDomain, isOnSubdomain, domainForLookup, organizationSlug]);

  const {
    data: organizationData,
    isLoading,
    status,
    error,
  } = usePerformRequest<{
    organization: IOrganizationProps;
    events: ISmallEventProps[];
    theme: IThemeProps;
  }>({
    method: endpoint.method,
    route: endpoint.route,
    doNotUseAuthorization: true,
  });

  const normalizedData = useMemo(() => {
    if (!organizationData) return null;
    const raw: any = organizationData;
    if (
      raw.data &&
      (raw.data.organization || raw.data.events || raw.data.theme)
    ) {
      return raw.data;
    }
    return raw;
  }, [organizationData]);

  // Set dynamic document title
  useEffect(() => {
    if (normalizedData?.organization?.name) {
      document.title = `${normalizedData.organization.name} - Events`;
    }
  }, [normalizedData?.organization?.name]);

  // Get the organization slug for authentication purposes
  // Use slug from URL if available, otherwise use slug from organization data
  const orgSlugForAuth = useMemo(() => {
    if (organizationSlug) {
      return organizationSlug;
    }
    // If using custom domain, get slug from organization data
    return normalizedData?.organization?.slug || "";
  }, [organizationSlug, normalizedData?.organization?.slug]);

  // Check authentication status for this organization and fetch/clear profile accordingly
  useEffect(() => {
    if (normalizedData?.organization && orgSlugForAuth) {
      const tokenKey = getOrgTokenKey(orgSlugForAuth);
      const token = localStorage.getItem(tokenKey);

      if (token) {
        // Token exists for this organization
        // Prefer cached org-scoped profile if available
        const profileKey = getOrgUserProfileKey(orgSlugForAuth);
        const storedProfile = localStorage.getItem(profileKey);

        if (storedProfile) {
          try {
            const parsed = JSON.parse(storedProfile);
            if (
              !userProfile ||
              userProfile.organizationSlug !== orgSlugForAuth
            ) {
              setUserProfile(parsed);
            }
          } catch (error) {
            console.error(
              "Failed to parse stored organization user profile:",
              error
            );
            localStorage.removeItem(profileKey);
            fetchUserProfile(orgSlugForAuth, normalizedData.organization);
          }
        } else if (
          !userProfile ||
          userProfile.organizationSlug !== orgSlugForAuth
        ) {
          // No cached profile – fetch from API
          fetchUserProfile(orgSlugForAuth, normalizedData.organization);
        }
      } else {
        // No token for this organization
        // Clear profile if it exists (user is logged in to a different organization)
        if (userProfile) {
          clearUserProfile(orgSlugForAuth);
        }
      }
    }
  }, [
    normalizedData?.organization,
    orgSlugForAuth,
    setUserProfile,
    fetchUserProfile,
    userProfile,
    clearUserProfile,
  ]);

  const events: Event[] = useMemo(() => {
    if (!normalizedData?.events) return [];
    return normalizedData.events.map(convertToEvent);
  }, [normalizedData?.events]);

  const handleViewMore = (event: ISmallEventProps) => {
    // Convert ISmallEventProps to Event format for selectedEvent if needed
    const convertedEvent: Event = {
      id: event.id.toString(),
      title: event.name,
      description: event.short_description || "",
      date: event.date,
      time:
        event.start_time && event.end_time
          ? `${event.start_time} - ${event.end_time}`
          : event.start_time || "TBD",
      location:
        event.type === "online"
          ? "Online Event"
          : event.type === "hybrid"
          ? "Hybrid Event"
          : "Location TBA",
      image: event.cover_image || "",
      category: event.category || "General",
      attendees: event.tickets_sold || 0,
      maxAttendees: event.total_tickets || undefined,
    };
    setSelectedEvent(convertedEvent);
  };

  // Check for custom Puck page data - must be called before early returns
  const customPageData = useMemo(() => {
    const organization = normalizedData?.organization;
    if (!organization) return null;
    const orgAny = organization as any;
    if (orgAny?.custom_page_schema) {
      try {
        const parsed =
          typeof orgAny.custom_page_schema === "string"
            ? JSON.parse(orgAny.custom_page_schema)
            : orgAny.custom_page_schema;
        if (parsed && parsed.content) {
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse custom page schema", e);
      }
    }
    // Fallback to localStorage for now
    const saved = localStorage.getItem(`puck-organization-${organization.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.content) {
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse saved schema", e);
      }
    }
    return null;
  }, [normalizedData?.organization]);

  // Show loading state
  if (isLoading) {
    return <FullPageLoader label="Loading organization..." />;
  }

  // Show not found page if organization doesn't exist (404) or no data
  // Check for 404 status, error response, or missing organization data
  const isNotFound =
    status === 404 ||
    error?.response?.status === 404 ||
    (!normalizedData && !isLoading && status !== "undefined") ||
    (normalizedData !== null && !normalizedData?.organization);

  if (isNotFound) {
    return <OrganizationNotFound />;
  }

  // Ensure we have valid organization data before rendering
  if (!normalizedData?.organization) {
    return <OrganizationNotFound />;
  }

  const theme = normalizedData.theme;
  const websiteIndex = theme?.website_index ?? 1;
  const organization = normalizedData.organization;

  // Special case: Render custom TheDRA page if organization slug is "thedra"
  const isTheDRA =
    organizationSlug?.toLowerCase() === "thedra" ||
    organization?.slug?.toLowerCase() === "thedra" ||
    organization?.name?.toLowerCase().includes("digital risk alliance");

  if (isTheDRA) {
    return (
      <OrganizationHomepageTheDRA
        organization={organization}
        events={normalizedData.events ?? []}
      />
    );
  }

  const renderTheme = () => {
    // If custom Puck page data exists, use PuckRenderer
    if (customPageData) {
      return (
        <PuckRenderer
          data={customPageData}
          pageType="organization"
          organization={organization}
          events={normalizedData.events ?? []}
        />
      );
    }

    // Otherwise, use default themes
    const commonProps = {
      organization: organization,
      events: normalizedData.events ?? [],
      theme: theme ?? null,
      onGetTicket: handleViewMore,
    };

    switch (websiteIndex) {
      case 1:
        return <OrganizationHomepageThemeOne {...commonProps} />;
      case 2:
        return <OrganizationHomepageThemeTwo {...commonProps} />;
      case 3:
        return <OrganizationHomepageThemeThree {...commonProps} />;
      case 4:
        return <OrganizationHomepageThemeFour {...commonProps} />;
      default:
        return <OrganizationHomepageThemeOne {...commonProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        organizationName={normalizedData?.organization?.name ?? ""}
        logoImage={normalizedData?.organization?.logo ?? ""}
        onLoginClick={() => setShowLogin(true)}
      />

      <div className="pt-20">{renderTheme()}</div>

      {/* Event Preview Modal */}
      {/* <AnimatePresence>
        {selectedEvent && (
          <EventPreviewModal
            event={selectedEvent}
            organization={normalizedData.organization}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </AnimatePresence> */}

      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            organizationName={normalizedData?.organization?.name ?? ""}
            organizationId={normalizedData?.organization?.id ?? 0}
            organizationSlug={orgSlugForAuth}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrganizationHomepage;
